import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import u from 'updeep';
import Frame from 'react-frame-component';

import Button from '../Button';
import SvgIcon from '../SvgIcon';
import Columnizer from '../Columnizer';
import LoaderBlock from '../LoaderBlock';
import ResizableTextField from '../ResizableTextField/ResizableTextField';
import { keyCodes, fontUrls, links, MAX_ZOOM, MIN_ZOOM, rtlLanguages, sanitizeHref } from '../../lib/constants';
import { getImageSize } from '../../lib/images';
import ModalControlButton from '../ModalControlButton';
import DeleteTranslationModal from './DeleteTranslationModal';
import SyncTranslationModal from './SyncTranslationModal';
import TranslationTipsModal from './TranslationTipsModal';
import LazyFontLoader from '../LazyFontLoader';
import Joyride from 'react-joyride';

import './OfflineTranslate.scss';
import './Modal.scss';

class OfflineTranslate extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isModalVisible: {
        delete: false,
        sync: false,
        tips: false
      },
      imageBlobs: {},
      currentPage: this.props.startPage || 2,
      isLoadingCss: true,
      hasNextPage: true,
      offlineTranslationText: [],
      tooltip: false,
      showJoyride: false,
      isTyping: false,
      zoomVal: 1
    };
    this.onOpenModal = this.onOpenModal.bind(this);
    this.onCloseModal = this.onCloseModal.bind(this);
  }

  onOpenModal(modalName) {
    this.setState(u({
      isModalVisible: {
        [modalName]: true
      },
    }, this.state));
  }

  onCloseModal(modalName) {
    this.setState(u({
      isModalVisible: {
        [modalName]: false
      }
    }, this.state));
  }

  onPlayTutorialClicked = () => {
    this.setState({ showJoyride: true })
  }

  onToggleToolTip = () => {
    this.setState({
      tooltip: !this.state.tooltip
    })
  }

  init = () => {
    if (document.getElementsByClassName("pb-offline-translate__frame")[0]) {
      let frameEl = document.getElementsByClassName("pb-offline-translate__frame")[0].contentWindow.document;
      let contentBoxEl = frameEl.getElementsByClassName('content')[0];
      let overlay1El = frameEl.getElementsByClassName('sp_h_text_overlay1')[0];
      let overlayEl = frameEl.getElementsByClassName('sp_h_text_overlay')[0];
      frameEl.querySelectorAll(".has_comments").forEach(el => {
        el.classList.remove("has_comments");
      })
      if(contentBoxEl) {
        //TODO: from backend
        contentBoxEl.style.zIndex="1";
        if (!overlayEl && !overlay1El) contentBoxEl.style.position="relative";
      }
    }
  }

  componentDidUpdate() {
    this.init();
  }

  componentDidMount() {
    document.addEventListener('keydown', this.onKeyDown);

    if (this.state.isLoadingCss) {
      return;
    }

    const iframeEl = ReactDOM.findDOMNode(this.iframeWrapperEl).querySelector('iframe');

    // Shitty cross-browser way of adding a keydown listener to an iframe's
    // document. On Firefox, you can't add the event until the iframe's "load"
    // event has fired, so you need to add the event inside the handler for the "load"
    // event. But, on IE and Safari you can't hook into the "load" event at all, so
    // now you have to do long polling on the iframe's document's readyState.
    const pollingTimeMs = 300;
    let timeElapsedMs = 0;
    const timer = setInterval(() => {
      // Clear the interval if, for some reason, the readyState never gets to
      // "complete".
      timeElapsedMs = timeElapsedMs + pollingTimeMs;
      if (timeElapsedMs >= pollingTimeMs * 20) {
        clearInterval(timer);
      }

      if (iframeEl.contentDocument.readyState === 'complete') {
        clearInterval(timer);
        iframeEl.contentDocument.addEventListener('keydown', this.onKeyDown);
      }
    }, pollingTimeMs);

  }

  componentWillMount() {
    this.syncPageWithOfflineTranslations();

    fetch(this.props.cssHref)
      .then(response => response.blob())
      .then(imageBlob => {
        const objectURL = URL.createObjectURL(imageBlob);
        this.setState({
          cssBlob: objectURL,
          isLoadingCss: false
        })
      })
      .catch(err => {
        // console.error(err);
        this.setState({ isLoadingCss: false });
      });

    let imageBlobs = this.state.imageBlobs;
    this.props.pages.forEach((page) => {
      if (page.coverImage && !this.state.imageBlobs[page.pageId]) {
        //https://medium.com/@gabriele.cimato/on-how-to-store-an-image-in-redux-d623bcc06ca7
        // for screen-landscape-lock function, vertical mode was opening but after locking it was taking the same image into landscape mode. Resulting in image pixelation. By taking max of width and height it was resolved. 
        const imageSize = getImageSize(Math.max(this.props.windowDimensions.width, this.props.windowDimensions.height))
        fetch(page.coverImage.sizes[imageSize - 1].url)
          .then(response => response.blob())
          .then(imageBlob => {
            let objectURL = URL.createObjectURL(imageBlob);
            imageBlobs[page.pageId] = objectURL;
            this.setState({
              imageBlobs: imageBlobs
            })
          });
      }
    });
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.cssHref !== nextProps.cssHref) {
      this.setState({ isLoadingCss: true })
      fetch(nextProps.cssHref)
        .then(response => response.blob())
        .then(imageBlob => {
          const objectURL = URL.createObjectURL(imageBlob);
          this.setState({
            cssBlob: objectURL,
            isLoadingCss: false
          })
        });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
  }

  onDescriptionChange = (boxId, e) => {
    let offlineTranslationText = this.state.offlineTranslationText.slice();
    const currentPage = this.props.assets.offlineStoryDetails.storyPages[this.state.currentPage - 1];
    const { pageId, pageType } = currentPage;
    let pageRecord = offlineTranslationText.find(page => page.pageId === pageId);

    if (!pageRecord) {
      pageRecord = {
        pageId,
        pageType
      };
      offlineTranslationText.push(pageRecord);
    }

    if (boxId) {
      if (!pageRecord.boxes) pageRecord.boxes = {};
      pageRecord.boxes[boxId] = e.target.value;
    }
    else {
      pageRecord.translationContent = `<p class="text-font-normal">${e.target.value.replace(/<[^>]*>/g, '')}</p>`;
    }

    this.setState(u({
      offlineTranslationText: offlineTranslationText
    }, this.state), () => {
      this.saveTranslation();
    });
  }

  onUserTyping = (e) => {
    let frameEl = document.getElementsByClassName("pb-offline-translate__frame")[0].contentWindow.document;

    if (e.type === "blur") {
      this.setState({ isTyping : false });      
      frameEl.querySelectorAll(`span[data-content-position]`).forEach(el => {
        el.style.opacity = "0.5"
      })
    }
    else if (e.type === "focus") {
      this.setState({ isTyping : true })
      let focusedEl = document.getElementById(e.target.id).parentNode.parentNode.previousSibling.childNodes;
      let length = focusedEl.length;
      if (length > 1) {
        let number = focusedEl[0].innerText.slice(1);
        frameEl.querySelectorAll(`span[data-content-position]`).forEach(el => {
          el.style.opacity = "0.5"
        })
        frameEl.querySelector(`span[data-content-position="${number}"]`).style.opacity = "1"
      }
    }
  }

  getLastEditedDate = () => {
    const today = new Date();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[today.getMonth()];
    const day = today.getDate();
    const year = today.getFullYear();
    const hour = today.getHours() > 12 ? today.getHours()-12 : (today.getHours() === 0 ? 12 : today.getHours());
    const minute = today.getMinutes() < 10 ? `0${today.getMinutes()}` : today.getMinutes();
    const AMPM = today.getHours() >= 12 ? "pm" :"am";

    return `${day} ${month} ${year}, ${hour}:${minute}${AMPM}`;  
  }

  saveTranslation = () => {
    const {
      book,
      userId,
      updateOfflineTranslationsWorkflow
    } = this.props;

    const profileId = userId || parseInt(localStorage.getItem('userId'), 10);
    book.lastEdited = this.getLastEditedDate();
    return updateOfflineTranslationsWorkflow(book, profileId, { targetLanguage: book.targetLanguage, content: this.state.offlineTranslationText });
  }

  onSaveClicked = () => {
    const {
      addSlimNotification,
      t
    } = this.props;
    this.saveTranslation()
      .then(() => {
        addSlimNotification({
          type: 'info',
          content: <div>{t('Book.saved-offline-draft-notification')} <SvgIcon name="offline" size="m"/> {t('Book.saved-offline-draft-notification-post-icon')}</div>
        })
      });
  }

  onTipsClicked = () => {
    this.onOpenModal('tips');
  }

  deleteTranslation = () => {
    const {
      book,
      userId,
      unsaveOfflineTranslationsWorkflow,
      windowDimensions
    } = this.props;

    const profileId = userId || parseInt(localStorage.getItem('userId'), 10);
    return unsaveOfflineTranslationsWorkflow(book, getImageSize(windowDimensions.width), profileId, book.targetLanguage)
  }

  onDeleteClicked = () => {
    const {
      addSlimNotification,
      t
    } = this.props;
    this.onCloseBook();
    this.deleteTranslation()
      .then(() => {
        localStorage.setItem('sw-offline-activity', 'offline' + Math.random());
        addSlimNotification({
          type: 'info',
          content: <div>{t('Book.deleted-offline-draft-notification')} <SvgIcon name="offline" size="m"/> {t('Book.deleted-offline-draft-notification-post-icon')}</div>
        })
      })
  }

  onKeyDown = (e) => {
    if (e.keyCode === keyCodes.rightArrow && !this.state.isTyping) {
      this.nextPage();
    } else if (e.keyCode === keyCodes.leftArrow && !this.state.isTyping) {
      this.prevPage();
    } else if (e.keyCode === keyCodes.esc) {
      e.stopPropagation();
      this.onCloseBook();
    }
  };

  onCloseBook = () => {
    this.props.closeOfflineTranslator();
  }

  hasNextPage = () => {
    return !this.props.pages[this.state.currentPage - 1].isLastStoryPage;
  }

  nextPage = () => {
    this.resetZoom();
    this.scrollTop();
    if (this.hasNextPage()) {
      this.setState({
        currentPage: this.state.currentPage + 1,
      }, () => {
        this.init();
      });
    }
  }

  hasPrevPage = () => {
    return !(this.state.currentPage - 1 < this.props.startPage);
  }

  prevPage = () => {
    this.resetZoom();
    this.scrollTop();
    if (this.hasPrevPage()) {
      this.setState({
        currentPage: this.state.currentPage - 1
      }, () => {
        this.init();
      });
    }
  }

  scrollTop = () => {
    let scrollEl = document.getElementsByClassName("pb-offline-translate__textarea-wrapper")[0];
    if (scrollEl) scrollEl.scrollTo(0,0);
  }

  syncPageWithOfflineTranslations = () => {
    const {
      book,
      userId,
      fetchOfflineTranslationsWorkflow,
    } = this.props;

    const profileId = userId || parseInt(localStorage.getItem('userId'), 10);
    fetchOfflineTranslationsWorkflow(book, profileId, book.targetLanguage)
      .then(response => {
        if (response && response.content) {
          this.setState(u({
            offlineTranslationText: response.content
          }, this.state));
        }
      })
  }

  onClickOnZoomOut = () => {
    this.zoom(-1);
  }

  onClickOnZoomIn = () => {
    document.getElementsByClassName("pb-offline-translate__frame")[0].contentWindow.document.getElementsByTagName("body")[0].style.overflow="auto";
    this.zoom(1);
  }

  resetZoom = () => {
    this.setState({ zoomVal: 1 }, () => {
      let zoomEl = document.getElementsByClassName("pb-offline-translate__frame")[0].contentWindow.document.getElementsByClassName("frame-content")[0];
      zoomEl.style.transform = `scale(${this.state.zoomVal})`;
      document.getElementsByClassName("pb-offline-translate__frame")[0].contentWindow.document.getElementsByTagName("body")[0].style.overflow="hidden";
    })
  }

  zoom = (num) => {
    let zoomVal = this.state.zoomVal + num;
    if (zoomVal < MIN_ZOOM || zoomVal > MAX_ZOOM) {
      return;
    }
    this.setState({ zoomVal: zoomVal }, ()=> {
      if (this.state.zoomVal === 1) {
        document.getElementsByClassName("pb-offline-translate__frame")[0].contentWindow.document.getElementsByTagName("body")[0].style.overflow="hidden";
      }
    })
    let zoomEl = document.getElementsByClassName("pb-offline-translate__frame")[0].contentWindow.document.getElementsByClassName("frame-content")[0];
    zoomEl.style.transformOrigin = "0 0";
    zoomEl.style.transform = `scale(${zoomVal})`;
  }

  handleJoyrideCallback = (data) => {
    const { 
      action, 
    } = data;

    if (action === "reset") {
      this.setState({ showJoyride: false })
    }
  }

  onSyncBookClicked = () => {
    const {
      t,
      book,
      addSlimNotification,
      postOfflineTranslateDataWorkflow,
    } = this.props;

    const offlineTranslationPayload = {
      story_id: book.id,
      language_name: book.targetLanguage,
      pages: this.state.offlineTranslationText,
      translation_partner_story: book.translation_partner_story ? true : null 
    }

    postOfflineTranslateDataWorkflow(book.id, book.targetLanguage, offlineTranslationPayload)
      .then(response => {
        if (response.ok) {
          this.onCloseBook();

          this.deleteTranslation()
            .then(() => {
              localStorage.setItem('sw-offline-activity', 'offline' + Math.random());
              addSlimNotification({
                type: 'info',
                content: t('Book.synced-offline-draft-notification')
              })
              setTimeout(function () {
                window.location.href = book.translation_partner_story ?  links.untranslatedStories() : links.myDrafts()
              }, 8000);
            })
        }
        else {
          addSlimNotification({
            type: 'danger',
            content: 'The story could not be synced. Please try again later'
          })
        }
      })
  }

  replaceImages = (root, pageId) => {
    if (!root) {
      return;
    }

    const imageEl = root.querySelector('.responsive_illustration');

    if (imageEl) {
      let imageBlobs = this.state.imageBlobs;
      if (imageBlobs[pageId]) {
        imageEl.setAttribute('src', imageBlobs[pageId]);
      }
      else {
        //If somehow the page's image has not been loaded while prefetching, will start the fetch again here
        // for screen-landscape-lock function, vertical mode was opening but after locking it was taking the same image into landscape mode. Resulting in image pixelation. By taking max of width and height it was resolved. 
        const imageSize = getImageSize(Math.max(this.props.windowDimensions.width, this.props.windowDimensions.height));
        fetch(imageEl.dataset[`size${imageSize}Src`])
          .then(response => response.blob())
          .then(imageBlob => {
            const objectURL = URL.createObjectURL(imageBlob);
            imageEl.setAttribute('src', objectURL);
            imageBlobs[pageId] = objectURL;
            this.setState({
              imageBlobs: imageBlobs
            })
          });
      }
    }
  }

  truncate = (contentText, number = 20) => {
    let text = contentText.replace(/[\r\n]/gi, ' ').trim();
    return (text.length > number) ? text.substr(0, number).concat('...') : text;
  }

  renderTextareaEls = (baseClassName, dir='ltr') => {
    const currentPage = this.props.assets.offlineStoryDetails.storyPages[this.state.currentPage - 1];
    const pageId = currentPage.pageId;
    const isRtl = this.props.assets.isLanguageRtl;

    let pageRecord = this.state.offlineTranslationText.find(page => page.pageId === pageId);
    if (!pageRecord) pageRecord = {};
    if (currentPage.boxes.length && pageRecord && !pageRecord.boxes) pageRecord.boxes = {};

    let textboxes = [];
    if (currentPage.content) textboxes.push({ content: currentPage.content, contentPosition: currentPage.contentPosition })
    if(currentPage.boxes) textboxes.push(...currentPage.boxes)

    return textboxes.sort((a, b) => a.contentPosition - b.contentPosition)
      .map(textbox => {
        return (
          <div>
            <div>
              {
                textbox.contentPosition
                  ?
                  <span className={`${baseClassName}__textbox-number`}>#{textbox.contentPosition}</span>
                  :
                  null
              }
              <span className={`${baseClassName}__textbox-map`}>
                (Ref:&nbsp;<span dir={isRtl ? 'rtl' : 'ltr'}>{this.truncate(textbox.content)}</span>)
              </span>
            </div>
            {
              textbox.boxId
                ?
                <ResizableTextField
                  id={textbox.boxId ? `${pageId}-${textbox.boxId}` : `${pageId}`}
                  value={pageRecord.boxes[textbox.boxId]}
                  onChange={this.onDescriptionChange.bind(this, textbox.boxId)}
                  onFocus={this.onUserTyping}
                  onBlur={this.onUserTyping}
                  dir={dir}
                />
                :
                <ResizableTextField
                  id={textbox.contentPosition ? `${pageId}-${textbox.contentPosition}` : `${pageId}`}
                  value={pageRecord.translationContent && pageRecord.translationContent.replace(/<[^>]*>/g, '')}
                  onChange={this.onDescriptionChange.bind(this, '')}
                  onFocus={this.onUserTyping}
                  onBlur={this.onUserTyping}
                  dir={dir}
                />
            }
          </div>
        )
      })
  }

  renderCoachmarkContent = (baseClassName, coachmarkTitle, coachmarkText , isRtl) => {
    return (
      <div dir={isRtl ? "rtl" : "ltr"} className={coachmarkTitle ? `${baseClassName}__tooltip-wrapper`: `${baseClassName}__tooltip-wrapper--no-header`}>
        <div className={`${baseClassName}__tooltip-image`}>
          <SvgIcon name="sw-logo" size="m-ml"/>
        </div>
        {coachmarkTitle ?
          <div className={`${baseClassName}__tooltip-content`}>
            <b>{coachmarkTitle}</b>
            <p>{coachmarkText}</p>
          </div>
          :
          <div className={`${baseClassName}__tooltip-content ${baseClassName}__tooltip-content--no-header`}>
            {coachmarkText}
          </div>
        }        
      </div>
    )
  }

  render() {
    const {
      t,
      orientation,
      language,
      offline,
      book,
      viewport,
      isSubmittingOfflineTranslateData,
      offlineBooks,
      userId,
      isRtl
    } = this.props;

    const {
      cssBlob,
      isLoadingCss,
      currentPage
    } = this.state;

    const baseClassName = 'pb-offline-translate';

    const mountTarget = 'story';

    const fontHref = fontUrls[language];
    const fontLink = fontHref ? `<link rel="stylesheet" type="text/css" href=${fontHref}>` : '';
    const cssLink = cssBlob ? `<link rel="stylesheet" type="text/css" href=${cssBlob} />` : '';
    // Added cssBlob link to initialContent Ref. https://github.com/ryanseddon/react-frame-component/issues/74

    const initialContent = `
      <!DOCTYPE html>
      <html>
        <head>
          ${cssLink}
          <base target="_parent">
          ${fontLink}          
        </head>
        <body style="overflow: hidden">
          <div id="${mountTarget}"></div>
          <script>
            document.addEventListener('contextmenu', function (e) {
              e.preventDefault();
            }, false); 
          </script>
        </body>
      </html>
    `;

    const classes = {
      [baseClassName]: true,
      [`${baseClassName}--${orientation}`]: orientation
    };

    const frameClasses = {
      [`${baseClassName}__frame`]: true,
    }

    const closeControlClasses = {
      [`${baseClassName}__control`]: true,
      [`${baseClassName}__control--close`]: true,
    }

    const nextControlClasses = {
      [`${baseClassName}__control`]: true,
      [`${baseClassName}__control--next`]: true,
    }

    const prevControlClasses = {
      [`${baseClassName}__control`]: true,
      [`${baseClassName}__control--prev`] : true,
    }

    if (isLoadingCss) {
      return (<div className={classNames(classes)} >
        <div className={`${baseClassName}__wrapper`}>
          <LoaderBlock />
        </div>
      </div>)
    }

    const lastPage = this.props.pages.find(page => page.isLastStoryPage);
    const bookTitle = book.title || book.name;

    let user = offlineBooks.coachmarkSeenCount.find(el => el.userId === userId);
    let coachmarkSeenCount = user ? user.count : null;
    
    const textDirection = rtlLanguages.includes(book.targetLanguage) ? 'rtl' : 'ltr';
    
    const JoyrideStyles = {
      options: { zIndex: 10000,
                  width: "400px" },
      overlay: { cursor: 'auto' },
      buttonClose: { display: 'none' },
      buttonSkip: { backgroundColor: "white",
                    color: '#2196F3',
                    position: "absolute",
                    bottom: "20px",
                    right: "40px",
                    marginTop:"0px",
                    fontSize: "16px" },
      buttonNext: (this.state.tooltip) ? 
                    {display: "none"}
                    :
                    { backgroundColor: "white",
                      color: '#2196F3',
                      position: "absolute",
                      bottom: "20px",
                      marginTop:"0px",
                      left: "67px" },
      tooltipContent: this.state.tooltip ?
                        { padding: '10px 10px 0px',
                          marginBottom: '-16px' }
                        :
                        { padding: '10px' },      
    }

    return (
      <div className={`${baseClassName}__modal-wrapper`}>
        { fontUrls[book.targetLanguage] ? <LazyFontLoader languages={[book.targetLanguage]} /> : null}
        {
          (coachmarkSeenCount <= 2) || this.state.showJoyride ?
          <Joyride
          steps={[
            {
              target: 'body',
              content: this.renderCoachmarkContent(baseClassName, t('OfflineTranslate.coachmark-step-1-title'), t('OfflineTranslate.coachmark-step-1') , isRtl),
              placement: 'center',
              offset :"0",
              disableBeacon: true, 
            },
            {
              target: '.pb-resizable-text-field__wrapper',
              content: this.renderCoachmarkContent(baseClassName, null, t('OfflineTranslate.coachmark-step-2'), isRtl),
              placement: 'left-end',
              offset :"0",
            },
            {
              target: '.pb-offline-translate__sync-btn',
              content: this.renderCoachmarkContent(baseClassName, t('OfflineTranslate.coachmark-step-3-title'), t('OfflineTranslate.coachmark-step-3') , isRtl),
              placement: 'bottom-start',
              offset :"0",
            }        
          ]}
          continuous={true}
          showSkipButton={( coachmarkSeenCount === 1 ) ? false : true}
          disableScrolling={true}
          hideBackButton={true}
          disableOverlayClose={true}
          disableCloseOnEsc={true}
          locale={{next: 'Ok!', last: 'Ok!'}}
          styles={JoyrideStyles}
          callback={this.handleJoyrideCallback}
        />
        : 
        null
        }
        {
          this.state.tooltip ?
          <Joyride
            steps={[
              {
                target: '.pb-offline-translate__sync-btn',
                content: offline ?
                          this.renderCoachmarkContent(baseClassName, t('OfflineTranslate.tooltip-sync-offline-title'), t('OfflineTranslate.tooltip-sync-offline') , isRtl)
                          :
                          this.renderCoachmarkContent(baseClassName, t('OfflineTranslate.tooltip-sync-online-title'), t('OfflineTranslate.tooltip-sync-online') , isRtl),
                placement: 'bottom-start',
                offset :"0",
                disableBeacon: true,                
              },
            ]}
            showSkipButton={false}
            floaterProps= {{
              disableAnimation: true,
            }}
            hideBackButton={true}
            disableScrolling={true}
            disableOverlay={true}
            locale={{last:"Ok!", close: "Ok!"}}
            styles={JoyrideStyles}        
          />
          : null
        }
        <div className={classNames(classes)} ref={ref => this.iframeWrapperEl = ref}>
          <div className={`${baseClassName}__wrapper`}>
            <div className={classNames(closeControlClasses)}>
              <ModalControlButton icon="close" label="Close" 
                onClick={this.onCloseBook} 
                outsideWrapper={viewport.medium} 
                closeOutsideWrapper={viewport.medium} 
              />
            </div>
              <div className={classNames(prevControlClasses)}>
                <ModalControlButton icon="chevron-left" label="Previous" 
                  section="offline-translate"
                  onClick={this.prevPage}
                  disabled={!this.hasPrevPage()}
                  outsideWrapper={viewport.medium}
                />
              </div>
              <div className={classNames(nextControlClasses)}>
                <ModalControlButton icon="chevron-right" label="Next" 
                  section="offline-translate"
                  onClick={this.nextPage}
                  disabled={!this.hasNextPage()}
                  outsideWrapper={viewport.medium}
                />
              </div>
            <div className={`${baseClassName}__header`}>
              <div className={`${baseClassName}__book-title`}>
                {this.truncate(bookTitle, 15)}
              </div>
              <div className={`${baseClassName}__buttons-wrapper`}>
                <Button
                  parentClassName={`${baseClassName}__tips-btn`}
                  label={t('global.tips')}
                  variant="primary-complement"
                  size="s"
                  onClick={this.onTipsClicked}
                />
                <Button
                  parentClassName={`${baseClassName}__delete-btn`}
                  label={t('global.delete')}
                  variant="primary-complement"
                  size="s"
                  onClick={() => this.onOpenModal('delete')}
                />
                <Button
                  parentClassName={`${baseClassName}__sync-btn`}
                  label={offline ? t('global.need-internet-to-sync') : t('global.sync')}
                  variant="primary-complement"
                  size="s"
                  disabled={offline}
                  loading={isSubmittingOfflineTranslateData}
                  loaderSize="s"
                  alignIconCenter
                  onClick={() => this.onOpenModal('sync')}
                  onMouseEnter={this.onToggleToolTip}
                  onMouseLeave={this.onToggleToolTip}
                />
                <Button
                  parentClassName={`${baseClassName}__save-btn`}
                  label={t('global.save')}
                  variant="primary"
                  size="s"
                  onClick={this.onSaveClicked}
                />
              </div>
            </div>

            <div className={`${baseClassName}__content`}>
              <Columnizer>
                <div className={`${baseClassName}__left-panel`}>
                  <div className={`${baseClassName}__language-wrapper`}>
                    <div className={`${baseClassName}__page-controls-wrapper`}>
                      <button className={`${baseClassName}__zoom-btn`} onClick={this.onClickOnZoomIn}>
                        <SvgIcon name="zoom-in" size="sm"/>
                      </button>
                      <button className={`${baseClassName}__zoom-btn`} onClick={this.onClickOnZoomOut}>
                        <SvgIcon name="zoom-out" size="sm"/>
                      </button>
                      <p className={`${baseClassName}__page`}>
                        {t('OfflineTranslate.page')}{currentPage}/{lastPage.pagePostion}
                      </p>
                    </div>
                    <p className={`${baseClassName}__original-lang`}>
                      {t('OfflineTranslate.original')}
                      <span className={`${baseClassName}__lang`}>
                        ({language})
                      </span>
                    </p>
                  </div>
                  <Frame
                    className={classNames(frameClasses)}
                    initialContent={initialContent}
                    mountTarget={`#${mountTarget}`}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: sanitizeHref(this.props.pages[currentPage - 1].html) }}
                      ref={ref => this.replaceImages(ref, this.props.pages[currentPage - 1].pageId)}
                    />
                  </Frame>
                </div>
                <div className={`${baseClassName}__right-panel`}>
                  <div className={`${baseClassName}__language-wrapper`}>
                    <p className={`${baseClassName}__translated-lang`}>
                      {t('OfflineTranslate.your-translation')}
                      <span className={`${baseClassName}__lang`}>
                       ({this.truncate(book.targetLanguage)})
                      </span>
                    </p>
                  </div>
                  <div className={`${baseClassName}__textarea-wrapper`}>
                    {this.renderTextareaEls(baseClassName, textDirection)}
                  </div>
                </div>
              </Columnizer>
            </div>
          </div>
        </div>

        {
          this.state.isModalVisible.delete
            ?
            <DeleteTranslationModal
              onConfirm={() => this.onDeleteClicked()}
              onClose={() => this.onCloseModal('delete')}
              viewport={viewport}
              isRtl={isRtl}
            />
            :
            null
        }

        {
          this.state.isModalVisible.sync
            ?
            <SyncTranslationModal
              onConfirm={() => this.onSyncBookClicked()}
              onClose={() => this.onCloseModal('sync')}
              viewport={viewport}
              isRtl={isRtl}
            />
            :
            null
        }

        {
          this.state.isModalVisible.tips
            ?
            <TranslationTipsModal
              onClose={() => this.onCloseModal('tips')}
              viewport={viewport}
              playTutorialClicked={() => {this.onPlayTutorialClicked()}}
              showPlayTutorial={coachmarkSeenCount > 2 ? true : false}
            />
            :
            null
        }

      </div>
    );
  }
}

OfflineTranslate.propTypes = {
  pages: PropTypes.array.isRequired,
  cssHref: PropTypes.string.isRequired,
  viewport: PropTypes.object.isRequired,
  orientation: PropTypes.string.isRequired,
};

export default OfflineTranslate;



// WEBPACK FOOTER //
// ./src/components/OfflineTranslate/OfflineTranslate.jsx